/* global CarouselSlider */

if (!customElements.get('product-card-image-slider')) {
  customElements.whenDefined('carousel-slider').then(() => {
    class ProductCardImageSlider extends CarouselSlider {
      constructor() {
        super();
        this.productCard = this.closest('product-card');
        this.productCardSwatches = this.productCard.querySelectorAll('.card__swatches .opt-btn');
        this.slides = Array.from(this.querySelectorAll('.slider__item'));

        this.productCard.addEventListener('change', this.handleSwatchChange.bind(this));
      }

      init() {
        super.init();

        this.slider.addEventListener('scroll', this.scrollInProgress.bind(this));

        // If swatches are enabled, mark the swatch anchors in the slideshow
        if (this.productCardSwatches) {
          const activeSlide = this.querySelector('.slider__item[aria-current]');
          const activeSwatchId = this.productCard.querySelector('.card__swatches .opt-btn:checked')?.dataset.mediaId || activeSlide?.dataset.mediaId;
          let activeSwatchSlide;

          if (activeSwatchId) {
            this.productCardSwatches.forEach((swatch) => {
              const swatchSlide = this.querySelector(`[data-media-id="${swatch.dataset.mediaId}"]`);
              if (swatchSlide) {
                swatchSlide.setAttribute('data-swatch-anchor', 'true');

                // Set the active set of slideshow images
                if (swatchSlide.dataset.mediaId === activeSwatchId) {
                  activeSlide.removeAttribute('aria-current');
                  swatchSlide.setAttribute('aria-current', 'true');
                  activeSwatchSlide = swatchSlide;
                }
              }
            });
          }

          if (activeSwatchSlide) this.handleSwatchChange(null, activeSwatchSlide.dataset.mediaId);
        } else {
          // Show the current slider item
          const activeSlide = this.querySelector('.slider__item[aria-current]');
          if (activeSlide) {
            this.handleSwatchChange(null, activeSlide.dataset.mediaId);
          } else {
            this.slider.slides[1]?.setAttribute('aria-current', 'true');
          }
        }
      }

      /**
       * Updates the visibility of slides based on the state of card swatches.
       * @param {string} mediaId - The media ID to match against the slides.
       */
      updateSlideVisibility(mediaId) {
        let hideSlide = true;
        let foundMediaId = false;
        this.slides.forEach((slide) => {
          if (!foundMediaId && slide.getAttribute('data-media-id') === mediaId) {
            hideSlide = false;
            foundMediaId = true;
          } else if (foundMediaId && slide.hasAttribute('data-swatch-anchor')) {
            hideSlide = true;
          }

          slide.toggleAttribute('hidden', hideSlide);
        });

        this.setButtonStates();
      }

      /**
       * Handles 'change' events in the product card swatches.
       * @param {object} evt - Event object.
       * @param {string} mediaId - The media ID to match against the slides.
       */
      handleSwatchChange(evt, mediaId) {
        const swatchMediaId = evt?.target.dataset.mediaId || mediaId;
        if (swatchMediaId) {
          this.updateSlideVisibility(swatchMediaId);

          const variantMedia = this.querySelector(`[data-media-id="${swatchMediaId}"]`);
          if (variantMedia) {
            const left = variantMedia.closest('.slider__item').offsetLeft;
            this.slider.scrollTo({ left, behavior: 'instant' });
          }
        }
      }

      /**
       * Sets the disabled state of the nav buttons.
       * Images loop, so the buttons are only disabled when there is a single visible slide.
       */
      setButtonStates() {
        if (!this.prevBtn && !this.nextBtn) {
          return;
        }

        const singleSlide = this.getVisibleSlides().length < 2;
        this.prevBtn.disabled = singleSlide;
        this.nextBtn.disabled = singleSlide;
      }

      /**
       * Gets the slides that are not hidden.
       * @returns {Element[]}
       */
      getVisibleSlides() {
        return this.slides.filter((slide) => slide.hidden !== true);
      }

      /**
       * Gets the visible slide closest to the current scroll position.
       * @returns {Element}
       */
      getCurrentSlide() {
        const visibleSlides = this.getVisibleSlides();
        const scrollLeft = this.slider.scrollLeft;
        return visibleSlides.reduce((closest, slide) => (
          Math.abs(slide.offsetLeft - scrollLeft) < Math.abs(closest.offsetLeft - scrollLeft) ? slide : closest
        ), visibleSlides[0]);
      }

      /**
       * Handles 'click' events on the nav buttons container.
       * Wraps around from the last slide to the first (and vice versa) keeping the slide direction.
       * @param {object} evt - Event object.
       */
      handleNavClick(evt) {
        if (!evt.target.matches('.slider-nav__btn')) return;
        if (this.isLooping) return;

        const visibleSlides = this.getVisibleSlides();
        if (visibleSlides.length < 2) return;

        const isNext = (evt.target.name === 'next' && !this.rtl) || (evt.target.name === 'prev' && this.rtl);
        const current = this.getCurrentSlide();
        const lastSlide = visibleSlides[visibleSlides.length - 1];

        if (isNext && current === lastSlide) {
          this.loopTo(current, visibleSlides[0], 'next');
          return;
        }

        if (!isNext && current === visibleSlides[0]) {
          this.loopTo(current, lastSlide, 'prev');
          return;
        }

        super.handleNavClick(evt);
      }

      /**
       * Scrolls the slider to a position without animation.
       * @param {number} left - Target scrollLeft.
       */
      jumpTo(left) {
        this.slider.style.scrollBehavior = 'auto';
        this.slider.scrollLeft = left;
        this.slider.style.scrollBehavior = '';
      }

      /**
       * Wraps to the target slide while keeping the slide direction.
       * The target is temporarily reordered next to the current slide so the slider keeps
       * moving in the same direction, then the real scroll position is restored without animation.
       * @param {Element} current - Current slide.
       * @param {Element} target - Slide to show.
       * @param {string} direction - 'next' or 'prev'.
       */
      loopTo(current, target, direction) {
        this.isLooping = true;

        // Move the target beside the current slide (after it for next, before it for prev).
        target.style.order = direction === 'next' ? '1' : '-1';

        // Keep the current slide in view after the reorder, then animate towards the target.
        this.jumpTo(current.offsetLeft);
        this.slider.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });

        // Once settled (or if the scroll was interrupted), restore the real layout at the target.
        this.waitForScrollEnd(target.offsetLeft).then(() => {
          target.style.order = '';
          this.jumpTo(target.offsetLeft);
          this.isLooping = false;
          this.currentIndex = this.slides.indexOf(target);
          this.slider.querySelector('[aria-current]')?.removeAttribute('aria-current');
          target.setAttribute('aria-current', 'true');
        });
      }

      /**
       * Resolves when the slider has settled at the given scroll position (or after a timeout).
       * @param {number} left - Expected scrollLeft.
       * @returns {Promise}
       */
      waitForScrollEnd(left) {
        return new Promise((resolve) => {
          const start = performance.now();
          let settledFrames = 0;
          const check = () => {
            if (Math.abs(this.slider.scrollLeft - left) < 1) {
              settledFrames += 1;
              if (settledFrames >= 2) {
                resolve();
                return;
              }
            } else {
              settledFrames = 0;
            }
            // Give up waiting if the scroll was interrupted (e.g. by a swipe).
            if (performance.now() - start > 1500) {
              resolve();
              return;
            }
            requestAnimationFrame(check);
          };
          requestAnimationFrame(check);
        });
      }

      /**
       * Handles 'scroll' events on the slider element.
       */
      scrollInProgress() {
        this.slider.querySelector('[aria-current]')?.removeAttribute('aria-current');
      }

      /**
       * Handles 'scroll' events on the slider element.
       */
      handleScroll() {
        if (this.isLooping) return;
        super.handleScroll();
        this.slides[this.currentIndex]?.setAttribute('aria-current', 'true');
      }
    }

    customElements.define('product-card-image-slider', ProductCardImageSlider);
  });
}
